import * as React from 'react';
import { Element, Transforms } from 'slate';
import styled from 'styled-components';
import { ReactEditor, useSlateStatic } from 'slate-react';
import DisplayText from '@sdk/components/ui/DisplayText';
import Row from '@sdk/components/ui/spacing/Row';
import Button from '@sdk/components/primitives/Button';
import View from '@sdk/components/primitives/View';

import Mention from './Mention';

const Heading1 = styled(View)`
  ${DisplayText} {
    color: ${({ theme }): string => theme.colors.primary};
    font-size: 20px;
    line-height: 24px;
  }
`;

const BlockQuoteContainer = styled(Row)`
  margin-top: 10px;
  margin-bottom: 10px;

  &:before {
    content: '';
    width: 3px;
    margin-right: 5px;
    background-color: ${({ theme }): string => theme.colors.separatorOnGray};
    border-radius: 1px;
  }
`;

const Link = styled(Button)`
  background-color: transparent;
  display: inline;
  white-space: pre-line;
  color: ${({ theme }): string => theme.colors.primary};

  &:hover {
    text-decoration: underline;
  }

  ${DisplayText} {
    display: flex;
    word-break: break-word;
    color: ${({ theme }): string => theme.colors.primary};
  }
`;

const QuoteTextContainer = styled(View)`
  flex: 1;
`;

const Code = styled.code`
  padding: 8px 10px;
  border-radius: 0;
  background-color: ${({ theme }): string => theme.colors.bodyBg};
  display: flex;
  flex-grow: 1;

  span {
    font-family: 'monospace, monospace';
  }
`;

type Props = {
  attributes: any;
  element: Element;
  onLinkClick: (url: string) => void;
};

const EditorElement: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const editor = useSlateStatic();
  const { attributes, children, element, onLinkClick } = props;

  switch (element.type) {
    case 'block-quote':
      return (
        <BlockQuoteContainer {...attributes}>
          <QuoteTextContainer>{children}</QuoteTextContainer>
        </BlockQuoteContainer>
      );
    case 'heading-1':
      return <Heading1 {...attributes}>{children}</Heading1>;
    case 'link':
      return (
        <Link
          onMouseDown={(event): void => {
            event.preventDefault();
            event.nativeEvent.stopImmediatePropagation();

            ReactEditor.focus(editor);

            const path = ReactEditor.findPath(editor, element);
            Transforms.select(editor, path);
            onLinkClick(element.link);
          }}
          {...attributes}>
          {children}
        </Link>
      );
    case 'mention': {
      return <Mention {...props} attributes={attributes} />;
    }
    case 'code-block': {
      return <Code {...attributes}>{children}</Code>;
    }

    default:
      return <View {...attributes}>{children}</View>;
  }
};

export default EditorElement;
